import { parseChatStreamError, ChatStreamError } from "../../../../services/chatService";
import { DraftPost, UIAction, SocialDescriptionItem, BlogPost, OutreachEmailDraft, ResearchDocumentData, GhlCall, ScannedCardContact, CardContactsAction, CardContactsAttachment } from "../types";

export interface StreamHandlerCallbacks {
  onStart?: (data: any) => void;
  onThinking?: (delta?: string, text?: string) => void;
  onToolSelected?: (toolName: string) => void;
  onToolExecuting?: (toolName: string) => void;
  onToolCall?: (data: any) => void;
  /** SGL-421: the Business Profile proposal the review tool prepared, sent before its tool_call. */
  onGbpPlan?: (plan: unknown) => void;
  /** SGL-421: the Visibility Manager changed a row of the open card from chat (revise_gbp_row). */
  onGbpRowUpdate?: (update: unknown) => void;
  /** SGL-421: the stage the Business Profile proposal just started (reading, categories, writing, checking). */
  onGbpPlanProgress?: (stage: string) => void;
  onGenerating?: () => void;
  onContent?: (delta: string, text?: string) => void;
  /** Perplexity: citations list, sent progressively as it changes during streaming (SGL-101). */
  onCitations?: (citations: string[]) => void;
  /** content_with_actions: full content + actions in one event (markdown/brainstorming turns). */
  onContentWithActions?: (content: string, actions: UIAction[]) => void;
  onSocialPosts?: (posts: DraftPost[], actions?: UIAction[]) => void;
  /** urls may be empty when error is set (image generation failed). isBatch flags a
   *  generate_image_batch completion (vs. a single generate_image call). */
  onImages?: (prompt: string, provider: string, urls: string[], actions?: UIAction[], error?: string, isBatch?: boolean) => void;
  onSuggestedActions?: (actions: UIAction[]) => void;
  /** UGC/reels: script event (delta = streaming chunk, fullText = full script from output_script tool). */
  onScript?: (delta?: string, fullText?: string) => void;
  /** UGC/reels: platform captions and tags from output_social_description tool. */
  onSocialDescription?: (items: SocialDescriptionItem[]) => void;
  /** Onboarding: structured business profile extracted by output_business_profile tool. */
  onBusinessProfile?: (data: Record<string, unknown>) => void;
  /** Blog: structured blog post emitted by the output_blog tool (Blog chip flow). */
  onBlog?: (blog: BlogPost) => void;
  /** Outreach email: structured draft emitted by the output_outreach_email tool (Outreach Email chip flow). */
  onOutreachEmail?: (email: OutreachEmailDraft) => void;
  /** GHL calls list emitted by the output_calls tool (AI Receptionist). */
  onCalls?: (calls: GhlCall[], truncated: boolean) => void;
  /** Scanned business cards + drafts emitted by the output_card_contacts tool (SGL-346). */
  onCardContacts?: (data: CardContactsAttachment) => void;
  /** Deep research: document uploaded to S3, ready to download. */
  onResearchDocument?: (doc: ResearchDocumentData) => void;
  /** Onboarding Phase 3: live crawl progress during RAG knowledge base build. */
  onCrawlProgress?: (pagesCompleted: number, pagesTotal: number, latestPageTitle: string, status: string) => void;
  onTitle?: (title: string, threadId?: string) => void;
  onEnd?: (data: any) => void;
  onError?: (error: ChatStreamError) => void;
  onToolError?: (toolName: string, error: string) => void;
}

export const handleStreamEvent = (
  event: string,
  data: any,
  callbacks: StreamHandlerCallbacks
): { shouldReturn: boolean; error?: ChatStreamError } => {
  switch (event) {
    case "start":
      callbacks.onStart?.(data);
      break;

    case "thinking":
      callbacks.onThinking?.(data.delta, data.text);
      break;

    case "tool_selected":
      callbacks.onToolSelected?.(data.tool_name || "");
      break;

    case "tool_executing":
      callbacks.onToolExecuting?.(data.tool_name || "");
      break;

    case "tool_call":
      callbacks.onToolCall?.(data);
      break;

    case "gbp_plan":
      callbacks.onGbpPlan?.(data?.plan);
      break;

    case "gbp_row_update":
      callbacks.onGbpRowUpdate?.(data);
      break;

    case "gbp_plan_progress":
      if (typeof data?.stage === "string") callbacks.onGbpPlanProgress?.(data.stage);
      break;

    case "generating":
      callbacks.onGenerating?.();
      break;

    case "social_posts":
      // Handle social_posts event
      // Backend format: { "event": "social_posts", "data": { "posts": [...] } }
      // In SSE, data line contains: {"posts": [...]} or the posts array directly
      let postsArray: any[] = [];
      
      if (Array.isArray(data)) {
        // If data is directly an array of posts
        postsArray = data;
      } else if (data.posts && Array.isArray(data.posts)) {
        // If data has a posts property
        postsArray = data.posts;
      } else if (data.data && data.data.posts && Array.isArray(data.data.posts)) {
        // If data is nested
        postsArray = data.data.posts;
      }

      const actions: UIAction[] = Array.isArray(data.actions)
        ? data.actions.map((a: any) => ({ type: a.type, label: a.label, payload: a.payload }))
        : [];
      if (postsArray.length > 0) {
        const draftPosts: DraftPost[] = postsArray.map((post: any, index: number) => ({
          temp_id: `post-${Date.now()}-${index}`,
          platform: post.platform || "UNKNOWN",
          title: post.title || "",
          content: post.post || post.content || "",
          image_url: post.image_url || "",
          hashtags: Array.isArray(post.hashtags) ? post.hashtags : [],
          call_to_action: post.call_to_action || "",
        }));
        callbacks.onSocialPosts?.(draftPosts, actions.length > 0 ? actions : undefined);
      }
      break;

    case "images":
      // Handle images event (success or failure: backend may send urls: [] and error when generation failed)
      let imageData = data;
      if (data.data) {
        imageData = data.data;
      }
      const imageActions: UIAction[] = Array.isArray(imageData.actions)
        ? imageData.actions.map((a: any) => ({ type: a.type, label: a.label, payload: a.payload }))
        : [];
      const imageUrls: string[] = Array.isArray(imageData.urls) ? imageData.urls : [];
      const imageError: string | undefined = imageData.error;
      const imageIsBatch: boolean = Boolean(imageData.is_batch);
      callbacks.onImages?.(
        imageData.prompt || "",
        imageData.provider || "unknown",
        imageUrls,
        imageActions.length > 0 ? imageActions : undefined,
        imageError,
        imageIsBatch
      );
      break;

    case "content_with_actions":
      if (data.content != null && Array.isArray(data.actions)) {
        const contentActions: UIAction[] = data.actions.map((a: any) => ({
          type: a.type,
          label: a.label,
          payload: a.payload,
        }));
        callbacks.onContentWithActions?.(String(data.content), contentActions);
      }
      break;

    case "suggested_actions":
      if (data.actions && Array.isArray(data.actions)) {
        const suggested: UIAction[] = data.actions.map((a: any) => ({
          type: a.type,
          label: a.label,
          payload: a.payload,
        }));
        callbacks.onSuggestedActions?.(suggested);
      }
      break;

    case "script":
      if (data.delta != null) {
        callbacks.onScript?.(typeof data.delta === "string" ? data.delta : "");
      }
      if (data.text != null) {
        callbacks.onScript?.(undefined, typeof data.text === "string" ? data.text : "");
      }
      break;

    case "research_document":
      if (data && typeof data === "object") {
        callbacks.onResearchDocument?.(data as ResearchDocumentData);
      }
      break;

    case "business_profile":
      if (data && typeof data === "object") {
        callbacks.onBusinessProfile?.(data as Record<string, unknown>);
      }
      break;

    case "blog":
      if (data && typeof data === "object") {
        callbacks.onBlog?.({
          title: data.title ?? "",
          excerpt: data.excerpt ?? "",
          content: data.content ?? "",
          author: data.author ?? "",
          featuredImage: data.featuredImage ?? "",
          author_bio: data.author_bio ?? "",
          author_linkedin: data.author_linkedin ?? "",
          tags: Array.isArray(data.tags) ? data.tags : [],
          category: data.category ?? "",
          published: data.published ?? false,
        });
      }
      break;

    case "outreach_email":
      if (data && typeof data === "object") {
        const ctaStyle = data.cta_style === "booking_link" || data.cta_style === "reply_only"
          ? data.cta_style
          : "hybrid";
        callbacks.onOutreachEmail?.({
          subject: data.subject ?? "",
          body: data.body ?? "",
          cta_style: ctaStyle,
          merge_fields: Array.isArray(data.merge_fields) ? data.merge_fields : [],
          booking_link: data.booking_link ?? "",
          campaign_context: data.campaign_context ?? "",
          notes: data.notes ?? "",
        });
      }
      break;

    case "calls":
      if (data && typeof data === "object" && Array.isArray(data.calls)) {
        const calls: GhlCall[] = data.calls.map((c: any) => ({
          contact_id: c.contact_id ?? null,
          contact_name: c.contact_name ?? "",
          phone: c.phone ?? "",
          direction: c.direction === "inbound" || c.direction === "outbound" ? c.direction : "unknown",
          status: c.status ?? "unknown",
          missed: !!c.missed,
          date: c.date ?? null,
          duration_seconds: typeof c.duration_seconds === "number" ? c.duration_seconds : null,
          conversation_id: c.conversation_id ?? null,
        }));
        callbacks.onCalls?.(calls, !!data.truncated);
      }
      break;

    case "card_contacts":
      if (data && typeof data === "object" && Array.isArray(data.contacts)) {
        const contacts: ScannedCardContact[] = data.contacts.map((c: any) => {
          // A draft is only usable with both halves; a subject with no body would
          // render as an approvable email that is half missing.
          const d = c?.draft;
          const draft =
            d && typeof d === "object" && typeof d.subject === "string" && typeof d.body === "string"
              ? { subject: d.subject, body: d.body }
              : null;
          return {
            name: c.name ?? null,
            title: c.title ?? null,
            company: c.company ?? null,
            email: c.email ?? null,
            phone: c.phone ?? null,
            mobile: c.mobile ?? null,
            website: c.website ?? null,
            draft,
            // Never infer sendable from the presence of an email: the backend
            // decides it, and defaulting to true here would offer to send a card
            // the backend already ruled out.
            sendable: c.sendable === true,
            reason: typeof c.reason === "string" && c.reason ? c.reason : null,
          };
        });
        const action: CardContactsAction =
          data.proposed_action === "save_only" || data.proposed_action === "draft_only"
            ? data.proposed_action
            : "save_and_send";
        callbacks.onCardContacts?.({
          contacts,
          captureContext: typeof data.capture_context === "string" ? data.capture_context : "",
          tags: Array.isArray(data.tags) ? data.tags.filter((t: any) => typeof t === "string" && t.trim()) : [],
          proposedAction: action,
        });
      }
      break;

    case "crawl_progress":
      callbacks.onCrawlProgress?.(
        typeof data.pages_completed === "number" ? data.pages_completed : 0,
        typeof data.pages_total === "number" ? data.pages_total : 0,
        typeof data.latest_page_title === "string" ? data.latest_page_title : "",
        typeof data.status === "string" ? data.status : "crawling"
      );
      break;

    case "social_description": {
      const raw = Array.isArray(data) ? data : data?.data;
      if (Array.isArray(raw) && raw.length > 0) {
        const items: SocialDescriptionItem[] = raw.map((item: any) => ({
          platform: item.platform ?? "",
          caption: item.caption ?? "",
          tags: Array.isArray(item.tags) ? item.tags : [],
        }));
        callbacks.onSocialDescription?.(items);
      }
      break;
    }

    case "content":
      if (data.delta) {
        callbacks.onContent?.(data.delta);
      } else if (data.text) {
        callbacks.onContent?.("", data.text);
      }
      break;

    case "citations":
      if (Array.isArray(data.citations)) {
        callbacks.onCitations?.(data.citations);
      }
      break;

    case "title":
      callbacks.onTitle?.(data.title, data.thread_id);
      break;

    case "end":
      // Terminal conditions the backend reports on the `end` event rather than as a separate
      // `error` event (an `error` event would stop the read loop before `end` is processed).
      // MODEL_REFUSAL:   the model produced no content — usually a safety refusal.
      // MODEL_TRUNCATED: the model ran out of output budget before writing anything. Only set
      //                  when the turn is empty; a truncated turn that DID produce text carries
      //                  `truncated: true` with no `code`, so it renders normally below.
      if (
        data.reason === "brand_details_required" ||
        data.code === "BRAND_DETAILS_REQUIRED" ||
        data.code === "MODEL_REFUSAL" ||
        data.code === "MODEL_TRUNCATED"
      ) {
        const parsedError = parseChatStreamError(data);
        callbacks.onError?.(parsedError);
        return { shouldReturn: true, error: parsedError };
      }
      callbacks.onEnd?.(data);
      break;

    case "error":
      const parsedError = parseChatStreamError(data);
      callbacks.onError?.(parsedError);
      return { shouldReturn: true, error: parsedError };

    case "tool_error":
      callbacks.onToolError?.(data.tool_name || "unknown", data.error || "Unknown error");
      break;

    default:
      // Fallback: handle as content if it has text/delta
      if (data.delta || data.text || data.content) {
        const content = data.delta || data.text || data.content;
        callbacks.onContent?.(content);
      }
      break;
  }

  return { shouldReturn: false };
};
